<script lang="ts" setup>
import type { HTMLAttributes } from "vue";
import { ErrorMessage } from "vee-validate";
import { inject, toValue } from "vue";
import { useI18n } from "vue-i18n";
import { cn } from "@/lib/utils";
import { translateFormMessage } from "@/utils/formRules";
import { FORM_MESSAGE_FORMAT } from "./injectionKeys";
import { useFormField } from "./useFormField";

const props = defineProps<{
  class?: HTMLAttributes["class"];
}>();

// Edited from shadcn-vue: schemas carry i18n keys as their messages (see
// utils/formRules.ts), so the message is translated here, and a plain string
// the schema wrote out in full passes through unchanged. text-danger, not
// text-destructive, per the colour rule.
const { name, formMessageId } = useFormField();
const { t, te } = useI18n();
// te() checks only the active locale, and a key that lives only in en-AU still
// resolves through the fallback.
const isKey = (message: string) => te(message) || te(message, "en-AU");
// A field can reword its own messages (FORM_MESSAGE_FORMAT, injectionKeys.ts).
const format = inject(FORM_MESSAGE_FORMAT, undefined);
const render = (message: string | undefined) =>
  message ? (format?.(message) ?? translateFormMessage(message, t, isKey)) : "";
</script>

<template>
  <ErrorMessage
    v-slot="{ message }"
    :id="formMessageId"
    data-slot="form-message"
    as="p"
    :name="toValue(name)"
    :class="cn('text-sm text-danger', props.class)"
  >
    {{ render(message) }}
  </ErrorMessage>
</template>
